import { removeTrapFocus, trapFocus, getFocusableElements } from '~/global';
import { defineElement } from '~/utils/gw-utils';

class CustomModalDialog extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    this.addEventListener('click', (event) => {
      console.log(event.target);

      if (
        event.target.nodeName === 'MODAL-DIALOG' ||
        event.target.classList.contains('ModalClose') ||
        event.target.hasAttribute('modal-close')
      ) {
        console.log('hide');
        this.hide();
      }
    });

    this.addEventListener('modal-reinit-focus', this.reiniTrapFocus.bind(this));
  }

  show(opener) {
    // Store the opener element to return focus to when closing the modal
    this.openedBy = opener;

    // Store the opener in a data attribute for backup recovery
    if (opener && opener.id) {
      this.setAttribute('data-opened-by', opener.id);
    }

    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    this.dispatchEvent(new CustomEvent('modal-open', { bubbles: true }));

    // Initialize proper focus management that allows screen readers to read all elements
    setTimeout(() => {
      this.initializeFocusManagement();
    }, 100)
  }

  initializeFocusManagement() {
    // Get all focusable elements in proper DOM order
    const focusableElements = getFocusableElements(this);

    if (focusableElements.length === 0) return;

    // Focus on the first element (usually the close button)
    const firstElement = focusableElements[0];

    // We need to let the screen reader read the title first
    const title = this.querySelector('#e-gift-card-preview-title');
    if (title) {
      // Set initial focus to the heading for screen readers to announce it
      setTimeout(() => {
        // First trap focus to ensure keyboard navigation stays within the modal
        trapFocus(this, firstElement);
      }, 100);
    } else {
      trapFocus(this, firstElement);
    }

    // Add special handling for the focus trap end element
    const focusTrapEnd = this.querySelector('.focus-trap-end');
    if (focusTrapEnd) {
      focusTrapEnd.addEventListener('focus', () => {
        if (focusableElements.length > 0) {
          // Redirect focus to the first focusable element
          focusableElements[0].focus();
        }
      });
    }
  }

  reiniTrapFocus(event) {
    const focusElement = event.detail?.focusElement || this.querySelector('button');
    trapFocus(this, focusElement);
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');

    // Multi-level fallback for returning focus to the opener element
    // First try: Use this.openedBy if it exists and is focusable
    if (this.openedBy && typeof this.openedBy.focus === 'function') {
      setTimeout(() => {
        this.openedBy.focus();
      }, 50);
      removeTrapFocus();
      return;
    }

    // Second try: Use the data-opened-by attribute if available
    const openerId = this.getAttribute('data-opened-by');
    if (openerId) {
      const openerById = document.getElementById(openerId);
      if (openerById && typeof openerById.focus === 'function') {
        setTimeout(() => {
          openerById.focus();
        }, 50);
        removeTrapFocus();
        return;
      }
    }

    // Third try: Look for localStorage fallback
    try {
      const lastOpenerId = localStorage.getItem('lastModalOpener');
      if (lastOpenerId) {
        const lastOpener = document.getElementById(lastOpenerId);
        if (lastOpener && typeof lastOpener.focus === 'function') {
          setTimeout(() => {
            lastOpener.focus();
          }, 50);
          removeTrapFocus();
          return;
        }
      }
    } catch (e) {
      console.warn('Could not retrieve lastModalOpener from localStorage', e);
    }

    // Fourth try: Look for modal opener by selector pattern
    const modalId = this.id;
    const modalOpeners = document.querySelectorAll(`[data-modal="#${modalId}"]`);
    if (modalOpeners.length > 0) {
      const button = modalOpeners[0].querySelector('button');
      if (button && typeof button.focus === 'function') {
        setTimeout(() => {
          button.focus();
        }, 50);
        removeTrapFocus();
        return;
      }
    }

    // Fifth try: Try general selectors
    const modalTrigger = document.querySelector(
      `[aria-controls="${modalId}"], [data-modal-open="${modalId}"], button[aria-haspopup="dialog"]`
    );
    if (modalTrigger && typeof modalTrigger.focus === 'function') {
      setTimeout(() => {
        modalTrigger.focus();
      }, 50);
    }

    // Always remove trap focus at the end
    removeTrapFocus();
  }
}
defineElement('custom-modal-dialog', CustomModalDialog);
